import React, { useMemo } from 'react';
import styled from 'styled-components';
import { CohortRowRelatedInfo } from '../../CohortRowUI';
import {
  CohortRowBetweenOperatorValues,
  CohortRowOperatorMenu,
  CohortRowSingleOperatorValue,
} from '../../CohortRowOperators';
import { useCohortRowContext } from '../../CohortRowContext';
import { assertCondition } from '../../../../../../utils/types';
import {
  RelationalOperatorKey,
  RetentionCohortOperatorKey,
  SimpleCohortTypeKey,
} from '../../../../../../utils/apollo/graphqlSchema';
import { useAppSelector } from '../../../../../../redux/hooks';
import { cohortConfigTypeSelector } from '../../../../../../redux/selectors/builder';
import useAnalysisBuilderInfo from '../../../../../../redux/selectorHooks/useAnalysisBuilderInfo';
import { RETENTION_CUSTOM_BRACKET_OPERATOR_TYPES } from '../../utils';
import { formatTimeUnit } from '../../../../../../utils';
import BuilderTimeBracketInput from '../../BuilderTimeBracketInput';
import RetentionCohortReturnedOperators from './RetentionCohortReturnedOperators';
import * as BuilderUI from '../../../../BuilderUI';

const OperatorText = styled(BuilderUI.BuilderFieldLabel)`
  align-self: center;
  margin-right: 0;
`;

const RetentionCohortOperators = () => {
  const { rowMeta, cohortRow } = useCohortRowContext();
  const {
    analysisId,
    simpleCohortTypeKey,
    operator,
    operatorValues,
    returnedOperator,
  } = cohortRow;
  assertCondition(simpleCohortTypeKey === SimpleCohortTypeKey.Retention);
  const cohortConfigSelector = useAppSelector((state) =>
    cohortConfigTypeSelector(state, simpleCohortTypeKey),
  );
  assertCondition(cohortConfigSelector.__typename === 'RetentionCohortConfig');
  const { operators, returnedOperators } = cohortConfigSelector;
  const analysisBuilderInfo = useAnalysisBuilderInfo(analysisId);
  const builderInfo = analysisBuilderInfo?.query;

  const retentionOperators = useMemo(() => {
    if (!builderInfo) {
      return operators;
    }

    assertCondition(builderInfo.__typename === 'RetentionQuery');
    const customTimeBrackets = builderInfo.customTimeBrackets;

    if (!customTimeBrackets.length) {
      return operators;
    }

    // If user select Retention with custom time brackets,
    // filter 'RETAINED_BETWEEN' operator
    return operators.filter((operator) => {
      if (customTimeBrackets.length > 0) {
        return operator.key !== RetentionCohortOperatorKey.RetainedBetween;
      }
      return true;
    });
  }, [builderInfo, operators]);

  const isRetentionCustomBracketOperator =
    operator &&
    RETENTION_CUSTOM_BRACKET_OPERATOR_TYPES.includes(
      operator as RetentionCohortOperatorKey,
    ) &&
    builderInfo?.__typename === 'RetentionQuery' &&
    builderInfo.customTimeBrackets.length > 0;

  return (
    <CohortRowRelatedInfo>
      <CohortRowOperatorMenu operators={retentionOperators} />
      {operator === RetentionCohortOperatorKey.Returned && (
        <RetentionCohortReturnedOperators
          returnedOperators={returnedOperators}
        />
      )}
      {operator !== RetentionCohortOperatorKey.Retained &&
        operator !== RetentionCohortOperatorKey.NotRetained && (
          <>
            {operator !== RetentionCohortOperatorKey.Returned &&
              !isRetentionCustomBracketOperator && (
                <OperatorText>
                  {formatTimeUnit(
                    builderInfo?.__typename === 'RetentionQuery' &&
                      builderInfo.retentionUnit
                      ? builderInfo.retentionUnit
                      : undefined,
                  )}
                </OperatorText>
              )}
            {(operator === RetentionCohortOperatorKey.Returned &&
              returnedOperator === RelationalOperatorKey.Between) ||
            operator === RetentionCohortOperatorKey.RetainedBetween ? (
              <CohortRowBetweenOperatorValues />
            ) : isRetentionCustomBracketOperator ? (
              <BuilderTimeBracketInput
                customTimeBrackets={builderInfo.customTimeBrackets}
                retentionUnit={builderInfo.retentionUnit}
                rowMeta={{ ...rowMeta, numberIndex: 0 }}
                type={'operatorValues'}
                value={operatorValues?.[0] ?? null}
              />
            ) : (
              <CohortRowSingleOperatorValue />
            )}
            {operator === RetentionCohortOperatorKey.Returned && (
              <OperatorText>times</OperatorText>
            )}
          </>
        )}
    </CohortRowRelatedInfo>
  );
};

export default RetentionCohortOperators;
